import type { OhlcData, Time } from "lightweight-charts";
import { ema } from "indicatorts";
import dayjs from "dayjs";

import type { QuestCandleData } from "../../../../context/quest/api";
import type { CandleData } from "../context";
import type { TIndicator, TMovingAverage } from "../../../../data/live-charts/config";
import type { Expr, ExprBinary } from "../../../../data/live-charts/expression";

export const getSeriesData = (data: QuestCandleData[]): OhlcData<Time>[] => {
  return data
    .sort((a, b) => a[0] - b[0])
    .map(([timestamp, open, close, low, high]) => {
      const time = (timestamp / 1000) as Time;
      return { time, open, close, high, low };
    });
};

export const aggregateData = (data: readonly CandleData[], period: number, type: TMovingAverage["type"]) => {
  const aggregatedData = data.map((d, idx) => {
    if (idx < period) return null;
    const periodData = data.slice(idx - period, idx);
    const value = periodData.reduce((acc, d) => acc + (d.close ?? 0), 0) / period;
    return { time: d.time, value: value };
  });

  const filtered = aggregatedData.filter(Boolean);

  if (type === "Simple") {
    return filtered;
  }

  if (type === "Exponential") {
    const values = filtered.map((d) => d.value);
    const calculated = ema(values, { period });
    return filtered.map((d, idx) => ({
      ...d,
      value: calculated[idx],
    }));
  }

  return filtered;
};

// How many candles of lookback an indicator needs before it can produce its first value. A moving average (and the band/RSI indicators) can't be drawn for the first `period` candles of the loaded data; Stochastic chains %K over kPeriod, smooths it, then derives %D over period. We use the largest of these across all enabled indicators to size the off-screen warmup buffer so the indicator line always reaches the start of the visible range.
export function indicatorLookback(indicators: TIndicator[] | null | undefined): number {
  if (!indicators?.length) return 0;

  let max = 0;
  for (const indicator of indicators) {
    if (!indicator.show) continue;

    const lookback =
      indicator.stateType === "Stochastic"
        ? indicator.kPeriod + indicator.slowingPeriod + indicator.period
        : indicator.period;

    if (lookback > max) max = lookback;
  }

  return max;
}

// Convert ISO string to a month code (e.g. JUN23, AUG26 etc.)
export function periodFromToMonthCode(periodFrom: string) {
  const date = dayjs(periodFrom);
  return date.format("MMMYY").toUpperCase();
}

export function formatPeriodLabel(periodFrom: string) {
  const date = dayjs(periodFrom);
  if (!date.isValid()) {
    // If periodFrom ends in 2 digits, put a space before the last 2 digits.
    const match = periodFrom.match(/(.*?)(\d{2})$/);
    return match ? `${match[1]} ${match[2]}` : periodFrom;
  }
  return date.format("MMMYY").toUpperCase();
}

// SMA function that actually handles NaN values correctly xD
export function safeSMA(values: number[], period: number): number[] {
  return values.map((_, i) => {
    const range = values.slice(Math.max(0, i - period + 1), i + 1);
    const valid = range.filter((v) => !Number.isNaN(v));
    return valid.length < period ? Number.NaN : valid.reduce((a, b) => a + b, 0) / period;
  });
}

// An ExprBinary has left and right nodes, which can be either a value, product, or another ExprBinary. This function recursively extracts all products from the expression tree.
export function expressionProducts(expression: ExprBinary | null | undefined) {
  if (!expression) return [];

  const products: string[] = [];

  function traverse(node: Expr | null | undefined) {
    if (!node) return;

    switch (node.type) {
      case "product":
        if (!products.includes(node.product.id)) products.push(node.product.id);
        break;
      case "constant":
        break;
      case "subexpr":
        traverse(node.base);
        traverse(node.chain);
        break;
      case "expr":
        traverse(node.left);
        traverse(node.right);
        break;
    }
  }

  traverse(expression);
  return products;
}

export function chartProductIds(chart: { productId?: string; expression?: ExprBinary | null }): string[] {
  const ids = [chart.productId, ...expressionProducts(chart.expression)].filter((id): id is string => !!id);
  return [...new Set(ids)];
}
